// Angular Imports
import { DatePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Component, inject, OnDestroy } from "@angular/core";
import { Router, RouterLink, RouterOutlet } from "@angular/router";

// Ant Imports
import { NzMenuModule } from "ng-zorro-antd/menu";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzTypographyComponent } from "ng-zorro-antd/typography";
import { NzColDirective, NzRowDirective } from "ng-zorro-antd/grid";
import { NzDropdownDirective, NzDropdownMenuComponent } from "ng-zorro-antd/dropdown";

// Services
import { HotkeysService } from "@ngneat/hotkeys";
import { UserService } from "#services/user/user.service";
import { AuthService } from "#services/auth/auth.service";
import { ThemeService } from "#services/theme/theme.service";
import { LoaderService } from "#services/loader/loader.service";
import { NavigationService } from "#services/navigation/navigation.service";
import { TranslationService } from "#services/translation/translation.service";
import { FileManagerService } from "#services/file-manager/file-manager.service";
import { CookieBridgeService } from "#services/cookie-bridge/cookie-bridge.service";

//Pipe
import { TranslatePipe } from "#pipes/translate/translate.pipe";

// Components
import { GotoComponent } from "#components/goto/goto.component";
import { LoaderComponent } from "#components/loader/loader.component";
import { NavigationComponent } from "#components/navigation/navigation.component";
import { FileManagerModalComponent } from "#components/file-manager/file-manager-modal/file-manager-modal.component";

//RXJS
import { Subject, takeUntil } from "rxjs";

//Model
import { UserOrNull } from "#models/user";
import { TranslationLangs } from "#models/translations";

@Component({
    selector: "app-template-logged-in",
    imports: [
        RouterLink,
        FormsModule,
        RouterOutlet,
        NzIconModule,
        NzMenuModule,
        GotoComponent,
        TranslatePipe,
        NzLayoutModule,
        NzButtonModule,
        NzRowDirective,
        NzColDirective,
        NzSelectModule,
        LoaderComponent,
        NavigationComponent,
        NzDropdownDirective,
        NzTypographyComponent,
        NzDropdownMenuComponent,
        FileManagerModalComponent,
    ],
    providers: [DatePipe],
    styleUrl: "./template-logged-in.component.scss",
    templateUrl: "./template-logged-in.component.html",
})
export class TemplateLoggedInComponent implements OnDestroy {
    //service
    router = inject(Router);
    userService = inject(UserService);
    fileManagerService = inject(FileManagerService);

    //variables
    user!: UserOrNull;
    isCollapsed!: boolean;
    currentLanguage: string;
    private destroy$ = new Subject<void>();

    private readonly hotKeys = inject(HotkeysService);
    private readonly authService = inject(AuthService);
    private readonly themeService = inject(ThemeService);
    private readonly loaderService = inject(LoaderService);
    private readonly cookieService = inject(CookieBridgeService);
    private readonly navigationService = inject(NavigationService);
    private readonly translationService = inject(TranslationService);

    constructor() {
        this.userService.user$
            .pipe(takeUntil(this.destroy$))
            .subscribe((user) => (this.user = user));
        this.navigationService.init();
        this.currentLanguage = this.translationService.currentLanguage;

        this.setHotkeys("shift.m", () => this.collapsedChanged());
        this.setHotkeys("shift.t", async () => await this.toggleTheme());

        this.isCollapsed = JSON.parse(
            this.cookieService.get("app.navigation.isCollapsed") || "false"
        );
    }

    get loaderServiceState() {
        return this.loaderService.state();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setHotkeys(key: string, callback: () => void) {
        this.hotKeys.removeShortcuts(key);
        this.hotKeys
            .addShortcut({ keys: key, preventDefault: true })
            .pipe(takeUntil(this.destroy$))
            .subscribe(callback);
    }

    async toggleTheme(): Promise<void> {
        await this.themeService.toggleTheme();
        this.cookieService.set("theme", this.themeService.themeName());
    }

    collapsedChanged() {
        this.isCollapsed = !this.isCollapsed;
        this.cookieService.set("app.navigation.isCollapsed", JSON.stringify(this.isCollapsed));
    }

    getThemeName() {
        return this.themeService.themeName();
    }

    closeFileManager() {
        this.fileManagerService.hide();
    }

    changeLanguage(lang: TranslationLangs[number]) {
        this.translationService.setLanguage(lang);
    }

    logout() {
        this.authService.logout();
    }
}
